export default function CoreProblem() {
  return (
    <section className="py-24 bg-slate-50">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              The Core Problem: The Consistency Paradox
            </h2>
            <p className="text-xl text-slate-700 leading-relaxed max-w-4xl mx-auto">
              Current AI policy focuses heavily on "unbiased data." However, our research identifies a deeper, structural flaw in how AI models are trained: <strong>The Consistency Paradox</strong>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Human Error */}
            <div className="bg-white p-8 shadow-lg border-t-4 border-red-800">
              <div className="w-16 h-16 flex items-center justify-center bg-red-800 text-white mb-6 mx-auto">
                <i className="ri-user-line text-3xl"></i>
              </div>
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
                Human Error
              </h3>
              <p className="text-slate-700 leading-relaxed">
                Human judgment is biologically inconsistent. Research shows that decisions in high-stakes environments (judicial rulings, medical triage) fluctuate wildly based on cognitive fatigue, hunger, and time of day.
              </p>
            </div>

            {/* AI Scale */}
            <div className="bg-white p-8 shadow-lg border-t-4 border-slate-900">
              <div className="w-16 h-16 flex items-center justify-center bg-slate-900 text-white mb-6 mx-auto">
                <i className="ri-robot-line text-3xl"></i>
              </div>
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
                AI Scale
              </h3>
              <p className="text-slate-700 leading-relaxed">
                Standard training methods (Inverse Reinforcement Learning) teach AI to mimic these human decisions. Because AI does not suffer from fatigue, it does not just copy our skills; it industrializes our cognitive failures, executing flawed or biased judgments with perfect, unblinking consistency.
              </p>
            </div>

            {/* The Result */}
            <div className="bg-white p-8 shadow-lg border-t-4 border-red-800">
              <div className="w-16 h-16 flex items-center justify-center bg-red-800 text-white mb-6 mx-auto">
                <i className="ri-alert-line text-3xl"></i>
              </div>
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
                The Result
              </h3>
              <p className="text-slate-700 leading-relaxed">
                <strong>"Bias Laundering"</strong>—where an algorithm takes historical discrimination (e.g., redlining or disparate impact) and launders it through complex mathematics to make it appear objective and neutral.
              </p>
            </div>
          </div>

          <div className="bg-slate-900 text-white p-10 text-center">
            <p className="text-xl lg:text-2xl leading-relaxed">
              AI doesn't just automate our decisions—it <strong>industrializes our inconsistencies</strong> and <strong>launders our biases</strong> through mathematical complexity.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
